import React, { useEffect, useRef, useState } from 'react';
import { Building2, Check, ChevronDown } from 'lucide-react';
import UserMenu from './UserMenu';
import { useOrganization } from '../context/OrganizationContext';

const organizationRoleLabel = (role) => {
  const normalized = String(role || '').trim().toLowerCase();
  if (!normalized) return 'Customer workspace';
  return `Organization ${normalized}`;
};

export default function CustomerHeader({ currentRoute, onLogout, actions }) {
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const workspaceRef = useRef(null);
  const {
    organizations,
    activeOrganization,
    organizationLoading,
    organizationError,
    selectOrganization
  } = useOrganization();

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (workspaceRef.current && !workspaceRef.current.contains(event.target)) {
        setWorkspaceOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  return (
    <header
      className="customer-header sticky top-0 z-30 bg-white/95 backdrop-blur-xl"
      style={{ height: 'var(--customer-header-height)' }}
    >
      <div className="flex h-full min-w-0 items-center justify-between gap-6 px-6 lg:px-8">
        <div className="flex h-10 min-w-0 flex-1 items-center gap-3">
          <div className="min-w-0">
          <h1 className="whitespace-nowrap text-[20px] font-semibold leading-5 tracking-tight text-slate-900">
            {currentRoute.title}
          </h1>
          </div>
          {actions}
        </div>

        <div className="flex min-w-0 shrink-0 flex-nowrap items-center justify-end gap-2">
          <div className="relative" ref={workspaceRef}>
            <button
              type="button"
              disabled={organizationLoading || !activeOrganization}
              onClick={() => organizations.length > 1 && setWorkspaceOpen((open) => !open)}
              className={`group mt-0 flex h-10 w-[220px] items-center gap-2.5 rounded-xl border bg-white px-3 text-left shadow-sm transition-all duration-200 ${
                organizations.length > 1
                  ? 'border-slate-200 hover:border-cyan-300 hover:shadow-md'
                  : 'border-slate-200'
              } disabled:cursor-default disabled:opacity-70`}
              aria-label="Active organization"
              aria-haspopup={organizations.length > 1 ? 'menu' : undefined}
              aria-expanded={organizations.length > 1 ? workspaceOpen : undefined}
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-cyan-50 ring-1 ring-cyan-100">
                <Building2 className="h-4 w-4 text-cyan-700" />
              </span>

              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">
                {organizationLoading
                  ? 'Loading workspace'
                  : activeOrganization?.name ||
                    (organizationError ? 'Workspace unavailable' : 'No workspace')}
              </span>

              {organizations.length > 1 && !organizationLoading && (
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 group-hover:text-slate-600 ${
                    workspaceOpen ? 'rotate-180' : ''
                  }`}
                />
              )}
            </button>

            {workspaceOpen && organizations.length > 1 && (
              <div
                className="absolute right-0 top-full z-50 mt-2 w-[300px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10"
                role="menu"
              >
                <div className="border-b border-slate-100 px-4 py-3">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                    Switch workspace
                  </div>
                </div>

                <div className="max-h-72 overflow-y-auto p-2">
                  {organizations.map((organization) => {
                    const active = organization.guid === activeOrganization?.guid;
                    return (
                      <button
                        key={organization.guid}
                        type="button"
                        onClick={() => {
                          selectOrganization(organization.guid);
                          setWorkspaceOpen(false);
                        }}
                        className={`mt-0 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition ${
                          active
                            ? 'bg-cyan-50 text-slate-900'
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                        role="menuitem"
                      >
                        <span
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                            active
                              ? 'bg-white text-cyan-700 shadow-sm'
                              : 'bg-slate-100 text-slate-500'
                          }`}
                        >
                          <Building2 className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold">
                            {organization.name}
                          </span>
                          <span className="block truncate text-[11px] text-slate-400">
                            {organizationRoleLabel(organization.membership_role)}
                          </span>
                        </span>
                        {active && (
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-cyan-600 text-white">
                            <Check className="h-3.5 w-3.5" />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <UserMenu onLogout={onLogout} role={activeOrganization?.membership_role} />
        </div>
      </div>
    </header>
  );
}
